
import { PickerPanelDateProps } from 'antd/lib/calendar/generateCalendar';
import { useComponentBridge } from 'apprise-frontend-core/utils/bridge';
import { noop } from 'apprise-frontend-core/utils/function';
import { usePrevious } from 'apprise-frontend-core/utils/hooks';
import { Sized, Wide, antsizeOf } from 'apprise-ui/component/model';
import { useChangeHelper } from 'apprise-ui/field/changehelper';
import { Field, useFieldProps } from 'apprise-ui/field/field';
import { ChangeTracked, Fielded } from 'apprise-ui/field/model';
import { useReadonlyHelper } from 'apprise-ui/field/readonlyhelper';
import { useResetHelper } from 'apprise-ui/field/resethelper';
import { useTimezone } from 'apprise-ui/time/time';
import { RemoveItemIcon } from 'apprise-ui/utils/icons';
import React, { lazy, Suspense, useEffect } from 'react';

export type DateBoxProps<T extends number | string> = Fielded<T> & Wide & Sized & ChangeTracked<T> & Partial<{

    children: T | undefined

    noClear: boolean

    noInput: boolean

    showTime: boolean | string

    timeFormat: string

    timezoned: true | string

    showNow?: boolean
    showToday? : boolean

    disableDate?: PickerPanelDateProps<Date>['disabledDate']
    disableTime?: PickerPanelDateProps<Date>['disabledTime']

}>


const DatePicker = lazy(() => import("./datefnsplugin"))

export const defaultFormats = ['dd/MM/yyyy', 'd/MM/yyyy', 'dd/M/yyyy', 'dd/MM/yy', 'd/MM/yy', 'dd/M/yy']
export const defaultTimeFormat = 'HH:mm:ss'

export const DateBox = <T extends number | string>(clientprops: DateBoxProps<T>) => {

    const { Indicator } = useComponentBridge()

    // changes to force a remount on reset.
    const [resetKey, resetSetKey] = React.useState(0)

    const props = useFieldProps(clientprops)

    const showNow = props.showNow ?? true

    const { pastMode, pastValue } = useChangeHelper(props)

    useReadonlyHelper(props)

    useResetHelper(props, {

        onReset: () => {

            onChange?.(undefined)       // won't reassert the default

            resetSetKey(s => ++s)         // forces a remount to do that.
        }
    })

    const { readonly, onChange: clientOnChange, showTime, timeFormat = defaultTimeFormat, defaultValue, children, placeholder, disabled, focusDecorations = [], noClear, noInput, minWidth, width = 200, size, disableDate, disableTime, timezoned, showToday=true,...rest } = props

    const numeric = typeof children === 'number'

    const latestValue = pastMode ? pastValue : (children ?? defaultValue)

    const previous = usePrevious(latestValue)

    // syncs field value with form reset.
    useEffect( () => {

        if (!latestValue && previous)
            resetSetKey(s => ++s)  
            

    },[latestValue,previous])

    const showTimeBool = showTime === true
    const showTimeFormat = showTimeBool ? timeFormat : undefined

    const defaultTime = typeof showTime === 'string' ? showTime : undefined

    const timezone = timezoned === 'string' ? timezoned : undefined
    
    const zone = useTimezone(timezone)


    const onChange = (value: Date | null | undefined ) => {

        // if we're date-only, antd keeps the time from previous value (or current time).
        // we strip it to get start of day (00:00) or the default time if provided.
        if (value && !showTimeBool) {
            const [h = 0, m = 0, s = 0] = defaultTime ? defaultTime.split(':').map(Number) : []
            value = new Date(value.getFullYear(), value.getMonth(), value.getDate(), h, m, s)
        }

        const zonedvalue = value ? timezoned ? zone.intern(value) : value : undefined

        const adapted =  (numeric ? zonedvalue?.getTime() : zonedvalue?.toISOString()) as T

       clientOnChange?.(adapted)

    }



    props.debug && console.log("datebox", { resetKey, defaultValue, children, onChange })

    const readonlyProps = readonly ? {

        open: false,
        inputReadOnly: true,
        clearIcon: false
    }

        : {

            clearIcon: <RemoveItemIcon />,
            inputReadOnly: false,

        }

 

    return <Field name='datebox' {...rest} focusDecorations={focusDecorations}>

        <Suspense fallback={<Indicator waiting />}>
            <DatePicker size={antsizeOf(size)} style={{ minWidth, width }} key={resetKey}

                disabledDate={disableDate}
                disabledTime={disableTime}
                showTime={showTimeBool ? { defaultValue: timezoned ? zone.extern(new Date()) : undefined , format: showTimeFormat } : false}
                allowClear={!noClear}
                placeholder={placeholder}
                value={latestValue ? timezoned ? zone.extern(new Date(latestValue)) :  new Date(latestValue) : null}
                format={showTimeBool ? defaultFormats.map(f => `${f} ${showTimeFormat}`) : defaultFormats}
                onChange={readonly ? noop :onChange}

                showNow={showNow}
                showToday={showToday}

                disabled={disabled}

                {...readonlyProps}

                inputReadOnly={noInput}
            />
        </Suspense>

    </Field>

}